import React from 'react';
import { Box, HStack, Tooltip } from '@chakra-ui/react';
import { Check, X, MinusCircle, AlertTriangle, Sparkles, User, CircleHelp } from 'lucide-react';

/**
 * ComplianceStatusChip
 *
 * Single source of truth for rendering a compliance decision on the
 * reviewer (and, in Phase 3.5, advisor) tables. Accepts the backend's
 * compliance payload directly — no prop gymnastics — so adding new
 * decision attributes is a one-line change here.
 *
 * Visual states:
 *   null            → "Not reviewed"  (muted gray)
 *   compliant       → "Compliant"     (green)
 *   needs_human_review → "Needs human"    (purple; AI or human-flagged ambiguity)
 *   is_stale=true   → amber overlay (wins against the base color) with
 *                     primary label from `stalePrimaryLabel` or the default
 *                     long wording. Tooltip still names the underlying verdict.
 *
 * The leading `decided_by` icon (Sparkles = AI, User = human) surfaces
 * who produced the verdict without another column. Hidden when unknown
 * (rare; covers pre-Phase-3.4 rows) to avoid a misleading placeholder.
 *
 * `compact`: drops the text label for advisor-side rendering where the
 * column header + summary strip already name the status globally — the
 * per-row chip just needs the state color + icon + human/AI marker.
 * The label + staleness reason are preserved as a hover tooltip so no
 * information is lost. Reviewer table leaves this off so the label
 * stays scannable while they're making decisions.
 */

const STATE_STYLES = {
  not_reviewed: {
    label: 'Not reviewed',
    icon: MinusCircle,
    bg: 'rgba(255,255,255,0.04)',
    color: 'rgba(255,255,255,0.55)',
    border: 'rgba(255,255,255,0.12)',
  },
  compliant: {
    label: 'Compliant',
    icon: Check,
    bg: 'rgba(52,211,153,0.12)',
    color: '#34D399',
    border: 'rgba(52,211,153,0.28)',
  },
  needs_human_review: {
    label: 'Needs human',
    icon: CircleHelp,
    bg: 'rgba(167,139,250,0.12)',
    color: '#A78BFA',
    border: 'rgba(167,139,250,0.38)',
  },
  non_compliant: {
    label: 'Non-compliant',
    icon: X,
    bg: 'rgba(239,68,68,0.12)',
    color: '#EF4444',
    border: 'rgba(239,68,68,0.30)',
  },
};

const STALE_STYLE = {
  bg: 'rgba(251,191,36,0.10)',
  color: '#FBBF24',
  border: 'rgba(251,191,36,0.30)',
};

/** Visible copy when ``is_stale`` (reviewer full chip — matches table filter wording). */
const STALE_PRIMARY_LABEL = 'Changed since last review';

const resolveState = (compliance) => {
  if (!compliance || !compliance.status) return 'not_reviewed';
  if (compliance.status === 'compliant') return 'compliant';
  if (compliance.status === 'needs_human_review') return 'needs_human_review';
  if (compliance.status === 'non_compliant') return 'non_compliant';
  return 'not_reviewed';
};

const DecidedByBadge = ({ decidedBy }) => {
  if (decidedBy !== 'ai' && decidedBy !== 'human') return null;
  const Icon = decidedBy === 'ai' ? Sparkles : User;
  const label = decidedBy === 'ai' ? 'AI reviewed' : 'Human reviewed';
  return (
    <Tooltip
      label={label}
      placement="top"
      openDelay={300}
      bg="rgba(20,20,30,0.95)"
      color="whiteAlpha.800"
      fontSize="xs"
      borderRadius="6px"
      px={2}
      py={1}
      hasArrow
    >
      <Box
        as="span"
        display="inline-flex"
        alignItems="center"
        justifyContent="center"
        flexShrink={0}
        opacity={0.75}
        aria-label={label}
      >
        <Icon size={10} strokeWidth={2.25} />
      </Box>
    </Tooltip>
  );
};

const ComplianceStatusChip = ({
  compliance,
  compact = false,
  /** When stale, overrides the default long chip label (e.g. reviewer table → "Changed"). */
  stalePrimaryLabel: stalePrimaryLabelProp,
}) => {
  const stateKey = resolveState(compliance);
  const base = STATE_STYLES[stateKey];
  const isStale = Boolean(compliance?.is_stale) && stateKey !== 'not_reviewed';

  const bg = isStale ? STALE_STYLE.bg : base.bg;
  const color = isStale ? STALE_STYLE.color : base.color;
  const border = isStale ? STALE_STYLE.border : base.border;
  const StatusIcon = isStale ? AlertTriangle : base.icon;

  // Tooltip: always surfaces the label in compact mode so hover still
  // tells you what the chip means; full mode only shows a tooltip when
  // it's carrying stale-specific context the label alone can't convey.
  const staleReason = isStale
    ? `${base.label} — message content changed since that review. Re-check before sending.`
    : null;
  const tooltip = compact ? (staleReason || base.label) : staleReason;

  const staleDisplay =
    stalePrimaryLabelProp != null && String(stalePrimaryLabelProp).length > 0
      ? stalePrimaryLabelProp
      : STALE_PRIMARY_LABEL;
  const displayLabel = !compact && isStale ? staleDisplay : base.label;
  const ariaLabel = isStale
    ? `${base.label} — content changed since last review`
    : base.label;

  const chip = (
    <Box
      px={compact ? 1.5 : 2}
      py={0.5}
      borderRadius="full"
      fontSize="11px"
      fontWeight="500"
      bg={bg}
      color={color}
      border="1px solid"
      borderColor={border}
      display="inline-flex"
      alignItems="center"
      whiteSpace="nowrap"
      maxW="100%"
      aria-label={ariaLabel}
    >
      <HStack spacing={compact ? 1 : 1.5} align="center">
        <Box as="span" display="inline-flex" alignItems="center" flexShrink={0}>
          <StatusIcon size={11} strokeWidth={2.25} />
        </Box>
        {!compact && (
          <Box as="span" isTruncated>
            {displayLabel}
          </Box>
        )}
        <DecidedByBadge decidedBy={compliance?.decided_by} />
      </HStack>
    </Box>
  );

  if (!tooltip) return chip;
  return (
    <Tooltip
      label={tooltip}
      placement="top"
      openDelay={280}
      bg="rgba(20,20,30,0.95)"
      color="whiteAlpha.800"
      fontSize="xs"
      borderRadius="6px"
      px={2}
      py={1}
      maxW="280px"
      hasArrow
    >
      {chip}
    </Tooltip>
  );
};

export default ComplianceStatusChip;
